import gsap from "gsap";
import ScrollToPlugin from "gsap/ScrollToPlugin";

gsap.registerPlugin(ScrollToPlugin);

class BlockStartContact {
  private block: HTMLAnchorElement;
  private isAnimating: boolean = false;
  private originalOverflow: string = "";
  private originalPaddingRight: string = "";

  constructor(blockEl: HTMLAnchorElement) {
    this.block = blockEl;
    this.handleClick = this.handleClick.bind(this);
  }

  private disableScroll() {
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    this.originalOverflow = document.body.style.overflow;
    this.originalPaddingRight = document.body.style.paddingRight;

    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = `${scrollbarWidth}px`;
  }

  private restoreScroll() {
    document.body.style.overflow = this.originalOverflow || "";
    document.body.style.paddingRight = this.originalPaddingRight || "";
  }

  private hideHeader() {
    const header = document.querySelector("header");
    if (header) {
      gsap.to(header, {
        opacity: 0,
        duration: 0.3,
        ease: "power2.out",
      });
    }
  }

  private animateSplitOpen() {
    const original = this.block.querySelector<HTMLElement>(".blockStartContact__original");
    const clone = this.block.querySelector<HTMLElement>(".blockStartContact__clone");

    if (!original || !clone) return;

    // Set initial state - both starting full
    gsap.set(original, {
      clipPath: "polygon(0% 0%, 100% 0%, 100% 50%, 0% 50%)", // Top half (from 0% to 50%)
    });
    gsap.set(clone, {
      clipPath: "polygon(0% 50%, 100% 50%, 100% 100%, 0% 100%)", // Bottom half (from 50% to 100%)
    });

    // Timeline for the split animation
    const tl = gsap.timeline({
      onComplete: () => {
        console.log("Split animation complete - navigating to:", this.block.href);
        // Navigate to the href after animation completes
        window.location.href = this.block.href;
      },
    });

    // Original: slides UP (top edge moves from 0% to -50%, bottom stays at 50%)
    tl.to(
      original,
      {
        clipPath: "polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)", // Slides up - collapses to top edge
        duration: 0.8,
        ease: "power2.inOut",
      },
      0
    );

    // Clone: slides DOWN (top stays at 50%, bottom edge moves from 100% to 150%)
    tl.to(
      clone,
      {
        clipPath: "polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)", // Slides down - collapses to bottom edge
        duration: 0.8,
        ease: "power2.inOut",
      },
      0
    );
  }

  private handleClick(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (this.isAnimating) return false;

    this.isAnimating = true;
    console.log("BlockStartContact clicked - scrolling to cover screen");

    // Hide header navigation
    this.hideHeader();

    // Disable scrolling immediately
    this.disableScroll();

    // Animate scroll to make the link cover the viewport
    gsap.to(window, {
      duration: 1.5,
      scrollTo: {
        y: this.block,
        offsetY: 0,
      },
      ease: "power2.inOut",
      onComplete: () => {
        console.log("Scroll animation complete - starting split animation");

        // Animate the split open effect
        this.animateSplitOpen();
      },
    });

    return false;
  }

  private resetAnimation() {
    const original = this.block.querySelector<HTMLElement>(".blockStartContact__original");
    const clone = this.block.querySelector<HTMLElement>(".blockStartContact__clone");

    if (original || clone) {
      // Kill any running animations
      gsap.killTweensOf([original, clone]);

      // Reset clip paths to initial state
      if (original) {
        gsap.set(original, { clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)" });
      }
      if (clone) {
        gsap.set(clone, { clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)" });
      }
    }

    // Reset header
    const header = document.querySelector("header");
    if (header) {
      gsap.set(header, { opacity: 1 });
    }

    // Restore scroll
    this.restoreScroll();
    this.isAnimating = false;
  }

  public init() {
    if (!this.block) return;

    // Use capture phase to intercept the click before other handlers
    this.block.addEventListener("click", this.handleClick, true);

    // Also add a regular listener as backup
    this.block.addEventListener("click", this.handleClick);
  }

  public destroy() {
    this.block.removeEventListener("click", this.handleClick, true);
    this.block.removeEventListener("click", this.handleClick);
  }
}

// Initialize multiple instances
function initBlockStartContact() {
  const blocks = Array.from(
    document.querySelectorAll<HTMLAnchorElement>(".blockStartContact")
  );
  if (blocks.length === 0) return;

  const instances: BlockStartContact[] = [];
  for (const el of blocks) {
    const instance = new BlockStartContact(el);
    instance.init();
    instances.push(instance);
  }
  (window as any)._blockStartContactInstances = instances;

  // Reset animations when navigating back via browser history
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
      // Page was restored from bfcache (back/forward cache)
      instances.forEach((instance) => {
        (instance as any).resetAnimation();
      });
    }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initBlockStartContact);
} else {
  initBlockStartContact();
}
